import { ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { ENDPOINT_ROOT } from 'src/Api/Endpoint';
import { useAxiosV2, axiosMethod } from 'src/hooks/useAxiosV2';
import { blimpAxiosClient } from 'src/utils/axiosInstances';
import { loadAuthUser } from './JWTContext';
import { PATH_WELCOME_PAGE } from 'src/routes/paths';
import useAuth from 'src/hooks/useAuth';
import { CustomerListDto, ImpersonatableUserDto } from 'src/Api/Dto/UserMeDto';
import { CustomerDto } from 'src/Api/Dto/CustomerDto';
import { IMPERSONATION_BANNER_HEIGHT } from 'src/config';


// ─── Context shape ────────────────────────────────────────────────────────────

type ImpersonateContextProps = {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;

    step: 0 | 1;

    customers: CustomerListDto[];
    customersLoading: boolean;
    selectedCustomer: CustomerListDto | null;
    onSelectCustomer: (customer: CustomerListDto) => void;
    onNextStep: () => void;
    onChangeCustomer: () => void;

    search: string;
    onSearch: (value: string) => void;
    filteredCustomers: CustomerListDto[];

    users: ImpersonatableUserDto[];
    usersLoading: boolean;
    selectedUser: ImpersonatableUserDto | null;
    onSelectUser: (user: ImpersonatableUserDto) => void;

    searchUser: string;
    onSearchUser: (value: string) => void;
    filteredUsers: ImpersonatableUserDto[];

    confirming: boolean;
    onConfirm: () => void;

    returning: boolean;
    onReturnToOriginal: () => void;

    onReset: () => void;

    isBannerOpen: boolean;
    closeBanner: () => void;

    bannerHeight: number;
    setBannerHeight: (height: number) => void;

    bannerOffset: number;

    onOpenWithCustomer: (customer: CustomerDto) => void;
};

const initialState: ImpersonateContextProps = {
    isOpen: false,
    onOpen: () => { },
    onClose: () => { },

    step: 0,

    customers: [],
    customersLoading: false,
    selectedCustomer: null,
    onSelectCustomer: () => { },
    onNextStep: () => { },
    onChangeCustomer: () => { },

    search: '',
    onSearch: () => { },
    filteredCustomers: [],

    users: [],
    usersLoading: false,
    selectedUser: null,
    onSelectUser: () => { },

    searchUser: '',
    onSearchUser: () => { },
    filteredUsers: [],

    confirming: false,
    onConfirm: () => { },

    returning: false,
    onReturnToOriginal: () => { },

    onReset: () => { },

    isBannerOpen: false,
    closeBanner: () => { },

    bannerHeight: IMPERSONATION_BANNER_HEIGHT,
    setBannerHeight: () => { },

    bannerOffset: 0,

    onOpenWithCustomer: () => { },
};

// ─── Context ──────────────────────────────────────────────────────────────────

const ImpersonateContext = createContext<ImpersonateContextProps>(initialState);

// ─── Provider ─────────────────────────────────────────────────────────────────

type ImpersonateProviderProps = { children: ReactNode };

function ImpersonateProvider({ children }: ImpersonateProviderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<0 | 1>(0);

    const [customers, setCustomers] = useState<CustomerListDto[]>([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerListDto | null>(null);

    const [search, setSearch] = useState('');
    const [searchUser, setSearchUser] = useState('');

    const [users, setUsers] = useState<ImpersonatableUserDto[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<ImpersonatableUserDto | null>(null);

    const [confirming, setConfirming] = useState(false);
    const [returning, setReturning] = useState(false);

    const [isBannerOpen, setIsBannerOpen] = useState(false);

    const [bannerHeight, setBannerHeight] = useState(IMPERSONATION_BANNER_HEIGHT);

    const bannerManuallyClosed = useRef(false);

    const { user } = useAuth();

    // ── Hooks API ────────────────────────────────────────────────────────────
    const { axiosFetch: fetchCustomers, data: customersData, loaded: customersLoaded } = useAxiosV2<CustomerListDto[]>({ onError: true });
    const { axiosFetch: fetchUsers, data: usersData, loaded: usersLoaded } = useAxiosV2<ImpersonatableUserDto[]>({ onError: true });
    const { axiosFetch: fetchImpersonate, loaded: impersonateLoaded, success: impersonateSuccess } = useAxiosV2({ onError: true });
    const { axiosFetch: fetchEndImpersonate, loaded: endImpersonateLoaded, success: endImpersonateSuccess } = useAxiosV2({ onError: true });

    // ── Banner segue lo stato reale dell'impersonation ────────────────────────
    useEffect(() => {
        if (!user?.impersonation) {
            bannerManuallyClosed.current = false;
            setIsBannerOpen(false);
            setBannerHeight(IMPERSONATION_BANNER_HEIGHT);
        } else if (!bannerManuallyClosed.current) {
            setIsBannerOpen(true);
        }
    }, [user?.impersonation]);

    // ── Load customers when dialog opens ─────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        loadCustomers();
    }, [isOpen]);

    async function loadCustomers() {
        setCustomersLoading(true);
        await fetchCustomers({
            url: `${ENDPOINT_ROOT.impersonation}/customers`,
            method: axiosMethod.get,
        });
    }

    useEffect(() => {
        if (!customersLoaded) return;
        setCustomers(customersData ?? []);
        setCustomersLoading(false);
    }, [customersData, customersLoaded]);

    // ── Load users response ───────────────────────────────────────────────────
    useEffect(() => {
        if (!usersLoaded) return;
        setUsers(usersData ?? []);
        setUsersLoading(false);
    }, [usersData, usersLoaded]);

    // ── Confirm impersonation response — reload after /xsrf + /me ───────────
    useEffect(() => {
        if (!impersonateLoaded) return;
        if (impersonateSuccess) {
            blimpAxiosClient.auth.get('/xsrf')
                .then(() => loadAuthUser())
                .then(() => window.location.replace(PATH_WELCOME_PAGE))
                .catch(() => window.location.replace(PATH_WELCOME_PAGE));
        } else {
            setConfirming(false);
        }
    }, [impersonateLoaded, impersonateSuccess]);

    // ── End impersonation response — reload after /xsrf + /me ───────────────
    useEffect(() => {
        if (!endImpersonateLoaded) return;
        if (endImpersonateSuccess) {
            blimpAxiosClient.auth.get('/xsrf')
                .then(() => loadAuthUser())
                .then(() => window.location.replace(PATH_WELCOME_PAGE))
                .catch(() => window.location.replace(PATH_WELCOME_PAGE));
        } else {
            setReturning(false);
        }
    }, [endImpersonateLoaded, endImpersonateSuccess]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    function handleSelectCustomer(customer: CustomerListDto) {
        setSelectedCustomer(customer);
    }

    async function handleNextStep() {
        if (!selectedCustomer) return;
        setSelectedUser(null);
        setStep(1);
        setUsersLoading(true);
        await fetchUsers({
            url: `${ENDPOINT_ROOT.impersonation}/customers/${selectedCustomer.id}/users`,
            method: axiosMethod.get,
        });
    }

    function handleChangeCustomer() {
        setStep(0);
        setSelectedUser(null);
        setUsers([]);
        setSearchUser('');
    }

    async function handleConfirm() {
        if (!selectedUser) return;
        setConfirming(true);
        await fetchImpersonate({
            url: `${ENDPOINT_ROOT.impersonation}/start/${selectedUser.id}`,
            method: axiosMethod.post,
        });
    }

    async function handleReturnToOriginal() {
        setReturning(true);
        await fetchEndImpersonate({
            url: `${ENDPOINT_ROOT.impersonation}/end`,
            method: axiosMethod.post,
        });
    }

    function handleReset() {
        setStep(0);
        setSearch('');
        setSearchUser('');
        setSelectedCustomer(null);
        setSelectedUser(null);
        setUsers([]);
        setCustomers([]);
        setConfirming(false);
    }

    function handleClose() {
        setIsOpen(false);
        handleReset();
    }

    function handleOpen() {
        handleReset();
        setIsOpen(true);
    }

    function handleCloseBanner() {
        bannerManuallyClosed.current = true;
        setIsBannerOpen(false);
    }

    function handleOpenWithCustomer(customer: CustomerDto) {
        handleReset();
        const customerListDto: CustomerListDto = {
            id: customer.id!,
            name: (customer as any).businessName ?? '',
            imageUrl: customer.imageUrl ?? undefined,
        };
        setSelectedCustomer(customerListDto);
        setIsOpen(true);
        setStep(1);
        setUsersLoading(true);
        fetchUsers({
            url: `${ENDPOINT_ROOT.impersonation}/customers/${customer.id}/users`,
            method: axiosMethod.get,
        });
    }

    // ── Derived state ─────────────────────────────────────────────────────────

    const bannerOffset = user?.impersonation && isBannerOpen ? bannerHeight : 0;

    const filteredCustomers = customers.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    const filteredUsers = users.filter((u) =>
        `${u.name} ${u.surname} ${u.role}`.toLowerCase().includes(searchUser.toLowerCase())
    );

    return (
        <ImpersonateContext.Provider
            value={{
                isOpen,
                onOpen: handleOpen,
                onClose: handleClose,

                step,

                customers,
                customersLoading,
                selectedCustomer,
                onSelectCustomer: handleSelectCustomer,
                onNextStep: handleNextStep,
                onChangeCustomer: handleChangeCustomer,

                search,
                onSearch: setSearch,
                filteredCustomers,

                users,
                usersLoading,
                selectedUser,
                onSelectUser: setSelectedUser,

                searchUser,
                onSearchUser: setSearchUser,
                filteredUsers,

                confirming,
                onConfirm: handleConfirm,

                returning,
                onReturnToOriginal: handleReturnToOriginal,

                onReset: handleReset,

                isBannerOpen,
                closeBanner: handleCloseBanner,

                bannerHeight,
                setBannerHeight,

                bannerOffset,

                onOpenWithCustomer: handleOpenWithCustomer,
            }}
        >
            {children}
        </ImpersonateContext.Provider>
    );
}

export { ImpersonateContext, ImpersonateProvider };